import {
    GameObjectReference,
    DefaultGameState,
    GameState,
    InventoryGameObjectReference
} from "../../../shared/types";
import { css } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

const styles: string = css`
    :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.6);
        z-index: 100;
        backdrop-filter: blur(4px);
    }

    .inventory-box {
        background: rgb(128 128 128);
        padding: 24px;
        border-radius: 16px;
        min-width: 320px;
        max-width: 90vw;
        color: #ffffff;
        box-shadow: 0 8px 24px #000000;
        animation: fadeIn 0.3s ease-in-out;
    }

    h2 {
        margin: 0;
        color: #ffffff;
        font-size: 1.75rem;
    }

    .top-part {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
    }

    button#closeButton {
        border: none;
        border-radius: 50%;
        background: #eee;
        color: #333;
        font-size: 1.25rem;
        width: 36px;
        height: 36px;
        cursor: pointer;
        transition: background 0.2s ease;
    }

    button#closeButton:hover {
        background: #ddd;
    }

    .item-list {
        display: flex;
        width: 300px;
        flex-wrap: wrap;
        justify-content: center;
        gap: 42px;
        padding: 2rem;
    }

    .item {
        width: 100px;
        height: 100px;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        border-radius: 50%;
        position: relative;
        cursor: pointer;
        transition: transform 0.2s ease;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        text-align: center;
        padding: 2px;
    }

    .item:hover {
        transform: scale(1.04);
        z-index: 10;
    }

    .item-label {
        position: absolute;
        bottom: -32px;
        width: 100%;
        text-align: center;
        color: #fff;
        font-weight: bold;
        font-size: 0.9rem;
        text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.8);
    }

    .detail-box {
        background: #ffffff35;
        padding: 16px;
        border-radius: 12px;
        margin-top: 20px;
        border: 1px solid #000000;
        animation: fadeIn 0.3s ease-in-out;
    }

    .image-container {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
    }


    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: scale(0.95);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
`;

export class InventoryComponent extends HTMLElement {
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState: DefaultGameState | undefined;

    private _inventoryOpen: boolean = false;
    private _items: InventoryGameObjectReference[] | undefined;
    private _selectedItem: InventoryGameObjectReference | null = null;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();

        void this.refreshGameState();
        this.listenForKeyPress();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update inventory items based on game state
     */
    private updateGameState(state: GameState): void {
        if (state.type === "switch-page") {
            return;
        }

        // Reset the component
        this._currentGameState = state;

        this.render();
    }

    /**
     * Listen for the "I" key to toggle the inventory
     */
    private listenForKeyPress(): void {
        window.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key.toUpperCase() === "I") {
                this.toggleInventory();
            }
        });
    }

    private updateInventory(): GameObjectReference[] | undefined {
        this._items = this._currentGameState?.inventory;
        return;
    }

    /**
     * Toggle inventory visibility
     */
    private toggleInventory(): void {
        this.updateInventory();
        this._inventoryOpen = !this._inventoryOpen;
        this.render();
    }

    /**
     * Render the inventory UI
     */
    private render(): void {
        if (this._items === undefined) {
            this._items = [];
        }

        this.shadowRoot!.innerHTML = `
            <style>
                ${styles}
                :host {
                    display: ${this._inventoryOpen ? "flex" : "none"};
                }
            </style>
            <div class="inventory-box">
                <div class="top-part">
                    <h2>Inventory</h2>
                    <button id="closeButton">X</button>
                </div>
                ${this._selectedItem
                    ? `
                        <div class="detail-box">
                            <h3>${this._selectedItem.name}</h3>
                            <div style="width: 100%; display: flex; justify-content: center; margin: 12px 0;">
                                <img src="/assets/img/items/${this._selectedItem.image}.png" style="max-width: 200px; border-radius: 1rem;" />
                            </div>
                            <p>${this._selectedItem.description || "Geen beschrijving beschikbaar."}</p>
                            <button id="backButton">Terug naar overzicht</button>
                        </div>
                    `
                    : `
                        <div class="item-list">
                            ${this._items.map((item: InventoryGameObjectReference) => `
                                <div class="item" 
                                    style="background-image: url('/assets/img/items/${item.image}.png');" 
                                    data-alias="${item.alias}">
                                    <div class="item-label">${item.name}</div>
                                </div>
                            `).join("")}
                        </div>
                    `
                }
            </div>
        `;

        // Close inventory
        this.shadowRoot!.querySelector("#closeButton")!.addEventListener("click", () =>
            this.toggleInventory()
        );

        // If a detail view is shown, bind back button click
        if (this._selectedItem) {
            const backButton: HTMLButtonElement = this.shadowRoot!.querySelector("#backButton") as HTMLButtonElement;
            if (backButton instanceof HTMLButtonElement) {
                backButton.addEventListener("click", () => {
                    this._selectedItem = null;
                    this.render();
                });
            }
        }
        else {
            // Bind click events for each item
            const itemElements: NodeListOf<HTMLDivElement> = this.shadowRoot!.querySelectorAll(".item");
            itemElements.forEach((element: Element) => {
                element.addEventListener("click", () => {
                    const alias: string | null = element.getAttribute("data-alias");
                    this._selectedItem = this._items!.find(item => item.alias === alias) ?? null;
                    this.render();
                });
            });
        }
    }
}
